/**
 * GlobalActivationDrawers — root-mounted overlay host for Space Activation.
 *
 * WHY THIS EXISTS (architecture):
 * Space Activation instructional + celebration drawers must be able to appear over ANY
 * route (the space page, the Create Question screen, invite/event pages). The codebase
 * already solves "UI that shows regardless of route" with a root-mounted, store-driven
 * overlay (see <GlobalShareSheet/> and <GlobalOnboarding/> in src/index.tsx). This mirrors
 * that: one instance mounted at the app root reads "which activation drawer is open" from
 * the zustand activationStore and renders the matching drawer. It is router-free, so any
 * navigation a drawer needs is performed by the opener (the activation tab / action sites),
 * never here.
 *
 * Sub-step drawers are built incrementally (one per step PR) and registered in
 * ./drawers/registry. The `subStep` branch renders the registered drawer for the open id,
 * or null when that sub-step's drawer isn't built yet. The `video`, `stepCelebration`, and
 * `activationComplete` kinds are all handled in the dispatch table below.
 */
import { lazy, Suspense, type ReactNode } from 'react';
import { useActivationStore } from '@stores/activationStore';
import type { OpenDrawer } from '@config/spaceActivation';
import { SUB_STEP_DRAWERS } from './drawers/registry';
import { ACTIVATION_COMPLETE_CELEBRATION, STEP_CELEBRATIONS } from '@config/spaceActivation';
import { SimpleCelebrationDrawer } from '@components/ui/celebration-drawer';

// Fullscreen video player for the `video` drawer kind (only watch_video today).
// Lazy so it never bloats the app bundle until an owner opens it.
const WatchVideoPlayer = lazy(() =>
  import('./WatchVideoPlayer').then(m => ({ default: m.WatchVideoPlayer })),
);

export function GlobalActivationDrawers(): ReactNode {
  // Selector subscriptions — re-render only when the open drawer changes.
  const open = useActivationStore(s => s.open);
  const close = useActivationStore(s => s.close);
  if (!open) return null;

  return <Suspense fallback={null}>{renderActivationDrawer(open, close)}</Suspense>;
}

/**
 * Dispatch table: maps the open-drawer kind to its explicit drawer component. Each branch
 * resolves to a distinct, self-contained, lazy()-loaded component — not
 * a boolean-prop mega-drawer.
 */
function renderActivationDrawer(
  open: OpenDrawer,
  close: (reason?: 'user' | 'programmatic') => void,
): ReactNode {
  switch (open.kind) {
    case 'subStep': {
      const SubStepDrawer = SUB_STEP_DRAWERS[open.id];
      if (!SubStepDrawer) return null;
      return (
        <SubStepDrawer
          onOpenChange={next => {
            if (!next) close('user');
          }}
          spaceId={open.spaceId}
          readIdentifier={open.readIdentifier}
          readInviteToken={open.readInviteToken}
          onPrimaryAction={open.onPrimaryAction}
          onSecondaryAction={open.onSecondaryAction}
        />
      );
    }
    case 'video':
      return (
        <WatchVideoPlayer
          onOpenChange={next => {
            if (!next) close('user');
          }}
          spaceId={open.spaceId}
        />
      );
    case 'stepCelebration': {
      const config = STEP_CELEBRATIONS[open.id];
      if (!config) return null;
      return (
        <SimpleCelebrationDrawer
          onOpenChange={next => {
            if (!next) close('user');
          }}
          {...config}
        />
      );
    }
    case 'activationComplete':
      return (
        <SimpleCelebrationDrawer
          onOpenChange={next => {
            if (!next) close('user');
          }}
          {...ACTIVATION_COMPLETE_CELEBRATION}
        />
      );
    default: {
      // Exhaustiveness guard: a new OpenDrawer kind makes this a compile error until handled.
      const _exhaustive: never = open;
      return _exhaustive;
    }
  }
}
